import { useState } from "react";
import { motion } from "framer-motion";
import { ProductCard } from "@/components/shop/ProductCard";
import { useProducts } from "@/hooks/useProducts";
import { CATEGORIES } from "@/data/content";

const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-");

const Filters = ({ value, onChange, counts }) => (
  <div role="tablist" aria-label="Filtra per categoria" className="flex flex-wrap gap-3">
    {CATEGORIES.map((c) => {
      const active = value === c;
      return (
        <button key={c} type="button" role="tab" aria-selected={active} onClick={() => onChange(c)} data-testid={`filter-${slugify(c)}`} className="relative h-12 rounded-full border border-navy/20 px-6 text-[15px] font-medium transition-colors hover:border-navy">
          {active && <motion.span layoutId="pf-filter-pill" className="absolute inset-0 rounded-full bg-navy" transition={{ type: "spring", stiffness: 380, damping: 34 }} />}
          <span className={`relative ${active ? "text-ivory" : "text-navy"}`}>
            {c} <span className="opacity-70">({counts[c] ?? 0})</span>
          </span>
        </button>
      );
    })}
  </div>
);

const LoadError = ({ onRetry, retrying }) => (
  <div className="mt-12 flex flex-col items-start gap-5 rounded-3xl border border-copper/20 bg-cream p-8 sm:flex-row sm:items-center sm:justify-between" data-testid="products-error">
    <p className="text-lg text-slate2">Non riusciamo a caricare i prodotti in questo momento. Riproviamo da soli tra pochi secondi.</p>
    <button type="button" onClick={onRetry} disabled={retrying} data-testid="products-retry" className="h-12 shrink-0 rounded-full bg-navy px-6 text-[16px] font-medium text-ivory transition-opacity disabled:opacity-50">
      {retrying ? "Sto riprovando…" : "Riprova ora"}
    </button>
  </div>
);

export const ProductGrid = ({ initialCategory = "Tutti" }) => {
  const { data: products = [], isLoading, isError, isFetching, refetch } = useProducts();
  const [category, setCategory] = useState(CATEGORIES.includes(initialCategory) ? initialCategory : "Tutti");
  const list = category === "Tutti" ? products : products.filter((p) => p.category === category);
  const counts = CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: c === "Tutti" ? products.length : products.filter((p) => p.category === c).length }), {});

  return (
    <div>
      <Filters value={category} onChange={setCategory} counts={counts} />
      {isError && products.length === 0 && <LoadError onRetry={() => refetch()} retrying={isFetching} />}
      <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5" data-testid="product-grid">
        {isLoading && Array.from({ length: 5 }).map((_, i) => <div key={i} className="aspect-[4/5] animate-pulse rounded-[1.75rem] bg-sand" />)}
        {list.map((p, i) => (
          <ProductCard key={p.slug} product={p} index={i} />
        ))}
      </div>
    </div>
  );
};
